import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { formatDay } from "@/lib/format";
import { useTerm } from "@/lib/terminology.jsx";
import { Bell, X, CheckCircle2, RotateCcw } from "lucide-react";

export default function Notifications() {
  const nav = useNavigate();
  const term = useTerm();
  const [rows, setRows] = useState([]);
  const [showCleared, setShowCleared] = useState(false);
  const [clearedRows, setClearedRows] = useState([]);

  const load = () =>
    api.get("/notifications").then(({ data }) => setRows(data));
  const loadCleared = () =>
    api.get("/notifications/cleared").then(({ data }) => setClearedRows(data));

  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (showCleared) loadCleared();
  }, [showCleared]);

  const activeCount = rows.filter((r) => r.status === "active").length;

  const dismiss = async (n) => {
    try {
      await api.post(`/notifications/${n.notification_id}/dismiss`);
      // v5.6 #7: 8-second undo toast
      toast("Notification dismissed", {
        duration: 8000,
        action: {
          label: "Undo",
          onClick: async () => {
            try {
              await api.post(`/notifications/${n.notification_id}/restore`);
              load();
              if (showCleared) loadCleared();
              toast.success(`${term("Notification")} restored`);
            } catch (err) {
              toast.error(err?.response?.data?.detail || "Failed to restore");
            }
          },
        },
      });
      load();
      if (showCleared) loadCleared();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to dismiss");
    }
  };

  const restore = async (n) => {
    try {
      await api.post(`/notifications/${n.notification_id}/restore`);
      toast.success("Restored");
      load();
      loadCleared();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Failed to restore");
    }
  };

  const openRow = (n) => {
    if (n.related_deal_id) {
      const path = n.notification_type === "gate_stage_alert"
        ? `/deals/${n.related_deal_id}?tab=gates`
        : `/deals/${n.related_deal_id}`;
      nav(path);
    }
  };

  return (
    <div className="p-8 max-w-4xl">
      <div className="cw-caption uppercase tracking-widest">Inbox</div>
      <h1 className="cw-h1 mt-1 inline-flex items-center gap-2">
        <Bell size={24} />
        {term("Notifications")}
        <span
          className="text-sm font-semibold rounded-full px-2.5 py-1"
          style={{ background: "var(--cw-secondary)", color: "var(--cw-primary-dark)" }}
          data-testid="notif-total"
        >
          {activeCount}
        </span>
      </h1>
      <p className="text-sm text-[var(--cw-muted)] mt-1">
        {term("Task")} reminders and {term("gate").toLowerCase()}-{term("stage").toLowerCase()} alerts. In-app only — no emails sent.
      </p>

      <div className="mt-4">
        <label className="text-xs inline-flex items-center gap-2">
          <input
            type="checkbox"
            checked={showCleared}
            onChange={(e) => setShowCleared(e.target.checked)}
            data-testid="notif-show-cleared"
          />
          Show cleared {term("notifications").toLowerCase()}
        </label>
      </div>

      <div className="mt-4 space-y-2" data-testid="notifications-list">
        {rows.length === 0 && (
          <div className="cw-card p-10 text-center text-[var(--cw-muted)]">
            No notifications yet.
          </div>
        )}
        {rows.map((n) => (
          <NotifRow key={n.notification_id} n={n} onOpen={openRow} onDismiss={dismiss} />
        ))}
      </div>

      {showCleared && (
        <>
          <div className="mt-8 cw-caption uppercase tracking-widest">
            Cleared ({clearedRows.length})
          </div>
          <div className="mt-2 space-y-2" data-testid="cleared-notifications-list">
            {clearedRows.length === 0 && (
              <div className="cw-card p-6 text-center text-xs text-[var(--cw-muted)]">
                Nothing cleared yet.
              </div>
            )}
            {clearedRows.map((n) => (
              <div
                key={n.notification_id}
                className="cw-card p-4 flex items-start gap-3"
                style={{ opacity: 0.55 }}
                data-testid={`cleared-notif-row-${n.notification_id}`}
              >
                <div
                  className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
                  style={{ background: "rgba(40,42,116,0.12)", color: "var(--cw-primary)" }}
                >
                  {n.notification_type === "task_reminder" ? <Bell size={14} /> : <CheckCircle2 size={14} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-semibold" style={{ color: "var(--cw-primary-dark)" }}>
                    {n.title}
                  </div>
                  {n.body && (
                    <div className="text-xs text-[var(--cw-muted)] mt-1 whitespace-pre-wrap">{n.body}</div>
                  )}
                  <div className="cw-caption mt-2">
                    Cleared {formatDay(n.cleared_at)}
                  </div>
                </div>
                {n.notification_type === "gate_stage_alert" && (
                  <button
                    onClick={() => restore(n)}
                    className="cw-btn-ghost text-xs inline-flex items-center gap-1"
                    data-testid={`notif-restore-${n.notification_id}`}
                  >
                    <RotateCcw size={12} /> Restore
                  </button>
                )}
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}

function NotifRow({ n, onOpen, onDismiss }) {
  const isDismissible =
    n.notification_type === "gate_stage_alert" ||
    n.notification_type === "rule_fire" ||
    n.notification_type === "export_ready";
  const isNavigable =
    (n.notification_type === "gate_stage_alert" && n.related_deal_id) ||
    (n.notification_type === "rule_fire" && n.related_deal_id);
  const iconBg =
    n.notification_type === "task_reminder"
      ? "rgba(243,191,92,0.25)"
      : n.notification_type === "rule_fire"
      ? "rgba(44,154,82,0.15)"
      : "rgba(40,42,116,0.12)";
  const iconColor =
    n.notification_type === "task_reminder"
      ? "#7A5900"
      : n.notification_type === "rule_fire"
      ? "#1F6E3A"
      : "var(--cw-primary)";
  return (
    <div
      className="cw-card p-4 flex items-start gap-3"
      style={{ opacity: n.status === "active" ? 1 : 0.55 }}
      data-testid={`notif-row-${n.notification_id}`}
    >
      <div
        className="w-8 h-8 rounded-full flex items-center justify-center shrink-0"
        style={{ background: iconBg, color: iconColor }}
      >
        {n.notification_type === "task_reminder" ? <Bell size={14} /> : <CheckCircle2 size={14} />}
      </div>
      <div className="flex-1 min-w-0">
        <button
          onClick={() => onOpen(n)}
          className="text-sm font-semibold text-left hover:underline"
          style={{ color: "var(--cw-primary-dark)" }}
          data-testid={`notif-title-${n.notification_id}`}
        >
          {n.title}
        </button>
        {n.body && (
          <div className="text-xs text-[var(--cw-muted)] mt-1 whitespace-pre-wrap">{n.body}</div>
        )}
        <div className="cw-caption mt-2">
          {formatDay(n.created_at)}
          {n.status === "cleared" && <span className="ml-2">· Cleared {formatDay(n.cleared_at)}</span>}
        </div>
      </div>
      {n.status === "active" && isDismissible && (
        <div className="flex gap-2 shrink-0">
          {isNavigable && (
            <button
              onClick={() => onOpen(n)}
              className="cw-btn-primary text-xs"
              data-testid={`notif-viewgates-${n.notification_id}`}
            >
              Open Deal
            </button>
          )}
          <button
            onClick={() => onDismiss(n)}
            className="cw-btn-ghost text-xs inline-flex items-center gap-1"
            data-testid={`notif-dismiss-${n.notification_id}`}
          >
            <X size={12} /> Dismiss
          </button>
        </div>
      )}
    </div>
  );
}
